// Motion polish for page changes. Every animation here is decoration: it never
// delays a tap or a save, and it is off for anyone whose device asks for
// reduced motion (Playwright runs with `reducedMotion: 'reduce'`, so no
// screenshot or layout measurement ever catches a page mid-fade).
//
// ⚠️ OPACITY ONLY on a page, never `transform`. `<Modal>` is not portaled — it
// renders `fixed` inside the page — and so does `<CaptureFab>`. A transform on
// any ancestor makes it the containing block of every `fixed` descendant, so a
// slide would carry the New-X button and a dialog opened on arrival (a register
// reopening its record from the URL on Back) along with the page.
//
// The Modal's own open animation lives in tailwind.config.ts (`sheet-in` /
// `dialog-in`): there a transform is safe, because the moving element is the
// card INSIDE the fixed backdrop, not an ancestor of it.

export const PAGE_FADE_MS = 150

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  } catch {
    return false
  }
}

/** Fade a freshly shown page in. Web Animations, not a CSS class: it needs no
 *  remount to replay (a `key` on the slot would remount the page and drop its
 *  state), and it leaves nothing behind once it finishes — no `fill`, so no
 *  lingering opacity or stacking context. Returns null when it did nothing. */
export function fadeInPage(el: HTMLElement | null): Animation | null {
  if (!el || typeof el.animate !== 'function' || prefersReducedMotion()) return null
  return el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: PAGE_FADE_MS, easing: 'ease-out' })
}

/** Fade in everything that comes AFTER `anchor` inside its page (or dialog):
 *  the siblings of the anchor and of each ancestor up to the page slot. A tab
 *  bar does not own its panel and has 25 callers, so it fades "whatever follows
 *  me" — never the header above it, never anything outside the page slot.
 *  Opacity only (fadeInPage). Returns how many elements it animated. */
export function fadeInAfter(anchor: HTMLElement | null): number {
  if (!anchor) return 0
  const stop = anchor.closest('[role="dialog"], .page-slot') ?? document.body
  let count = 0
  for (let n: HTMLElement | null = anchor; n && n !== stop; n = n.parentElement) {
    for (let s = n.nextElementSibling; s; s = s.nextElementSibling) {
      if (s instanceof HTMLElement && fadeInPage(s)) count++
    }
  }
  return count
}

export const EXIT_MS = 150

/** Play a dismiss: the backdrop fades out and the card fades (a centred
 *  dialog) or also drops 32px (a phone sheet). Resolves when both finish, or
 *  after a safety timeout — never rejects, never hangs.
 *
 *  Only for a DISMISS (X, Cancel, backdrop, Escape, a declined confirm). A close
 *  after a save unmounts at once: the SubmitOverlay already said "saved". The
 *  card may move because it sits INSIDE the fixed backdrop (see top of file).
 *  `fill: 'forwards'` because the caller removes the element right after, and
 *  it must not snap back to visible in between. */
/** Would playExit animate anything? False under reduced motion, without the
 *  Web Animations API (jsdom) or without both elements: the caller then closes
 *  exactly as it did before there was an exit — at once, with no closing state. */
export function exitAnimates(backdrop: HTMLElement | null, card: HTMLElement | null): boolean {
  return !!backdrop && !!card && !prefersReducedMotion() &&
    typeof backdrop.animate === 'function' && typeof card.animate === 'function'
}

/** Where a dismiss starts when the sheet was dragged: `y` px down, backdrop at
 *  `backdropOpacity`. Phone sheet only; a centred dialog ignores it. */
export interface ExitFrom { y: number; backdropOpacity: number }

export function playExit(backdrop: HTMLElement | null, card: HTMLElement | null, from?: ExitFrom): Promise<void> {
  if (!backdrop || !card || !exitAnimates(backdrop, card)) return Promise.resolve()
  const wide = window.matchMedia?.('(min-width: 640px)').matches ?? false
  const opts: KeyframeAnimationOptions = { duration: EXIT_MS, easing: 'ease-in', fill: 'forwards' }
  const cardFrames: Keyframe[] = wide
    ? [{ opacity: 1 }, { opacity: 0 }]
    : [
        { opacity: 1, transform: `translateY(${from?.y ?? 0}px)` },
        { opacity: 0, transform: `translateY(${Math.max(0, from?.y ?? 0) + 32}px)` },
      ]
  const anims = [
    backdrop.animate([{ opacity: wide ? 1 : (from?.backdropOpacity ?? 1) }, { opacity: 0 }], opts),
    card.animate(cardFrames, opts),
  ]
  return new Promise<void>(resolve => {
    const timer = setTimeout(resolve, EXIT_MS + 150)
    // A cancelled animation rejects `finished`; either way we are done.
    void Promise.allSettled(anims.map(a => a.finished)).then(() => { clearTimeout(timer); resolve() })
  })
}
